import SEOHead from "@/components/SEOHead";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import comparisonContent from "@/content/lumient-comparison.json";

const comparisonGuideJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: comparisonContent.title,
      description: comparisonContent.description,
      url: comparisonContent.canonical,
      dateModified: comparisonContent.dateModified,
      isPartOf: {
        "@type": "WebSite",
        name: "Lumient",
        url: "https://lumient.ai",
      },
      about: [
        "document AI",
        "RAG",
        "workflow automation",
        "data platforms",
        "enterprise agents",
        "operational records",
        "unstructured business evidence",
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: comparisonContent.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
  ],
};

const positioningSteps = [
  "Documents arrive as operational evidence.",
  "A reviewed workflow runs against that evidence.",
  "The affected record updates and keeps source evidence attached.",
];

const CONTAINER = "max-w-screen-2xl mx-auto px-6 lg:px-16";
const EYEBROW = "text-xs font-medium tracking-[0.2em] uppercase mb-5 text-primary";
const SECTION_HEADING =
  "font-serif font-semibold text-[2.25rem] leading-[1.05] tracking-[-0.02em] text-[#0B0B0B]";
const BODY_COPY = "text-[0.95rem] font-light leading-[1.65] text-muted-foreground";
const COMPARISON_COPY = "text-[0.95rem] font-light leading-[1.65] text-[oklch(0.40_0.012_75)]";
const LEFT_ANCHOR = "col-span-12 lg:col-span-3 lg:sticky lg:top-32 lg:self-start";
const RIGHT_CONTENT = "col-span-12 lg:col-span-8 lg:col-start-5";
const comparisonTitleMatch = comparisonContent.title.match(/^(Lumient)\s+(vs .+)$/);
const comparisonTitleLead = comparisonTitleMatch?.[1] ?? comparisonContent.title;
const comparisonTitleRest = comparisonTitleMatch?.[2];

const LumientComparison = () => (
  <div className="min-h-screen bg-background text-foreground flex flex-col">
    <SEOHead
      title={comparisonContent.title}
      description={comparisonContent.description}
      canonical={comparisonContent.canonical}
      jsonLd={comparisonGuideJsonLd}
    />

    <main className="flex-1 w-full">
      <article aria-labelledby="comparison-guide-title">
        <section className="bg-background border-b border-border">
          <div className={`${CONTAINER} pt-28 pb-14 lg:pt-36 lg:pb-20`}>
            <div className="max-w-4xl">
              <p className={`${EYEBROW} mb-6`}>
                {comparisonContent.eyebrow}
              </p>
              <h1
                id="comparison-guide-title"
                className="font-serif font-semibold text-[clamp(2.75rem,5.4vw,5.4rem)] leading-[0.98] tracking-[-0.035em] text-[#0B0B0B] mb-7"
              >
                {comparisonTitleRest ? (
                  <>
                    <span className="block">{comparisonTitleLead}</span>{" "}
                    <span className="block">{comparisonTitleRest}</span>
                  </>
                ) : (
                  comparisonTitleLead
                )}
              </h1>
              <p className="max-w-3xl text-[1.05rem] lg:text-[1.15rem] font-light leading-[1.65] text-muted-foreground">
                {comparisonContent.intro}
              </p>
            </div>
          </div>
        </section>

        <section className="bg-surface-subtle border-b border-border py-20 lg:py-28">
          <div className={CONTAINER}>
            <div className="grid grid-cols-12 items-start gap-y-12">
              <div className={LEFT_ANCHOR}>
                <h2 className="sr-only">Positioning</h2>
                <p className={EYEBROW}>
                  {comparisonContent.positioning.eyebrow}
                </p>
                <h3 className={`${SECTION_HEADING} mb-6`}>
                  {comparisonContent.positioning.heading}
                </h3>
                <p className={BODY_COPY}>
                  {comparisonContent.positioning.body}
                </p>
              </div>

              <aside className={`${RIGHT_CONTENT} self-stretch rounded-2xl border border-border bg-surface-panel p-6 lg:p-8 flex flex-col`}>
                <p className={EYEBROW}>
                  OPERATING MODEL
                </p>
                <ol className="flex-1 divide-y divide-border">
                  {positioningSteps.map((step, index) => (
                    <li key={step} className="grid grid-cols-[3rem_minmax(0,1fr)] items-start gap-4 py-5">
                      <span className="font-mono text-xs tracking-[0.12em] pt-[0.18em] text-[#C8900A]">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="text-[0.98rem] leading-[1.6] text-foreground">
                        {step}
                      </span>
                    </li>
                  ))}
                </ol>
              </aside>
            </div>
          </div>
        </section>

        <section className="bg-background border-b border-border py-20 lg:py-28">
          <div className={CONTAINER}>
            <div className="grid grid-cols-12 items-start gap-y-12">
              <div className={LEFT_ANCHOR}>
                <p className={EYEBROW}>
                  COMPARISON
                </p>
                <h2 className={`${SECTION_HEADING} mb-6`}>
                  Comparison
                </h2>
                <p className={`${COMPARISON_COPY} max-w-[19rem]`}>
                  The main distinction is what remains after a file, model run, or workflow step completes.
                </p>
              </div>

              <div className={`${RIGHT_CONTENT} grid grid-cols-1 lg:grid-cols-2 gap-4`}>
                {comparisonContent.comparisons.map((item, index) => (
                  <section
                    key={item.title}
                    className={`rounded-2xl border border-border bg-surface-panel p-5 lg:p-6 ${
                      index === comparisonContent.comparisons.length - 1 ? "lg:col-span-2" : ""
                    }`}
                  >
                    <h3 className="text-xs font-medium tracking-[0.2em] uppercase mb-4 text-primary">
                      {item.title}
                    </h3>
                    <p className="font-serif text-[1.35rem] lg:text-[1.45rem] font-semibold leading-[1.18] tracking-[-0.015em] text-[#0B0B0B] mb-3">
                      {item.subtitle}
                    </p>
                    <p className={BODY_COPY}>
                      {item.body}
                    </p>
                  </section>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-surface-subtle border-b border-border py-20 lg:py-28">
          <div className={CONTAINER}>
            <div className="grid grid-cols-12 items-start gap-y-12">
              <div className={LEFT_ANCHOR}>
                <h2 className="sr-only">When Lumient fits</h2>
                <p className={EYEBROW}>
                  {comparisonContent.fit.eyebrow}
                </p>
                <h3 className={`${SECTION_HEADING} mb-6`}>
                  {comparisonContent.fit.heading}
                </h3>
                <p className={BODY_COPY}>
                  {comparisonContent.fit.body}
                </p>
              </div>

              <ul className={`${RIGHT_CONTENT} border-y border-border`}>
                {comparisonContent.useCases.map((useCase) => (
                  <li key={useCase} className="grid grid-cols-[1rem_minmax(0,1fr)] gap-4 items-start py-5 border-t border-border first:border-t-0 text-[0.98rem] leading-[1.65] text-foreground">
                    <span aria-hidden="true" className="mt-[0.65em] h-1.5 w-1.5 rounded-full bg-[#C8900A]" />
                    <span>{useCase}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="bg-background border-b border-border py-20 lg:py-28">
          <div className={CONTAINER}>
            <div className="grid grid-cols-12 items-start gap-y-12">
              <div className={LEFT_ANCHOR}>
                <p className={EYEBROW}>
                  {comparisonContent.shortAnswers.eyebrow}
                </p>
                <h2 className={`${SECTION_HEADING} mb-6`}>
                  Short answers
                </h2>
              </div>

              <div className={RIGHT_CONTENT}>
                <div className="mb-8">
                  <p className="font-serif font-semibold text-[1.35rem] lg:text-[1.55rem] leading-[1.2] tracking-[-0.015em] text-[#0B0B0B] mb-3">
                    {comparisonContent.shortAnswers.heading}
                  </p>
                  <p className={BODY_COPY}>
                    {comparisonContent.shortAnswers.body}
                  </p>
                </div>

                <Accordion
                  type="single"
                  collapsible
                  className="rounded-2xl border border-border bg-surface-panel"
                >
                  {comparisonContent.faqs.map((faq) => (
                    <AccordionItem key={faq.question} value={faq.question} className="px-5 lg:px-6 border-border last:border-b-0">
                      <AccordionTrigger className="py-4 text-left font-serif text-[1.05rem] lg:text-[1.12rem] font-semibold leading-[1.25] tracking-[-0.01em] text-[#0B0B0B] hover:no-underline [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:text-muted-foreground">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="pb-5 pt-0 text-[0.95rem] font-light leading-[1.7] text-muted-foreground">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </div>
        </section>
      </article>

      <section className="bg-surface-inverse">
        <div className={`${CONTAINER} py-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8`}>
          <div>
            <h2 className="text-3xl font-normal text-surface-inverse-foreground tracking-tight mb-2">
              {comparisonContent.cta.heading}
            </h2>
            <p className="text-surface-inverse-muted text-base">
              {comparisonContent.cta.body}
            </p>
          </div>
          <a
            href={comparisonContent.cta.href}
            className="glow-button flex-shrink-0 px-7 py-3 text-sm font-semibold rounded-full whitespace-nowrap"
          >
            {comparisonContent.cta.label}
          </a>
        </div>
      </section>
    </main>
  </div>
);

export default LumientComparison;
